/** Piezas de UI que comparten las siete páginas. */
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatGs, formatGsCompact, parseGs } from '@/lib/currency';
import { cn } from '@/lib/utils';

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="text-2xl font-black tracking-tighter sm:text-3xl lg:text-4xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export function StatCard({ label, value, hint, icon: Icon, tone = 'default' }) {
  const tones = {
    default: 'text-foreground',
    positive: 'text-emerald-600 dark:text-emerald-400',
    negative: 'text-red-600 dark:text-red-400',
    warning: 'text-amber-600 dark:text-amber-400',
  };

  return (
    <div className="rounded-sm border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        {Icon && <Icon size={16} className="shrink-0 text-muted-foreground" />}
      </div>
      <p className={cn('mt-2 text-xl font-black tracking-tighter sm:text-2xl', tones[tone])}>
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="rounded-sm border border-dashed border-border bg-card px-6 py-12 text-center">
      {Icon && <Icon size={32} className="mx-auto mb-3 text-muted-foreground" />}
      <p className="font-bold">{title}</p>
      {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Loading({ label = 'Cargando…' }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
      <Loader2 size={16} className="animate-spin" />
      {label}
    </div>
  );
}

/**
 * Campo de dinero en guaraníes: muestra el número con separador de miles
 * mientras se escribe y devuelve un entero al formulario.
 */
export function MoneyInput({ value, onChange, ...props }) {
  return (
    <Input
      inputMode="numeric"
      value={value ? formatGsCompact(value) : ''}
      onChange={(event) => onChange(parseGs(event.target.value))}
      placeholder="0"
      {...props}
    />
  );
}

export function Money({ value, className, tone }) {
  const tones = {
    positive: 'text-emerald-600 dark:text-emerald-400',
    negative: 'text-red-600 dark:text-red-400',
  };
  return <span className={cn('tabular-nums', tone && tones[tone], className)}>{formatGs(value)}</span>;
}

export function SubmitButton({ loading, children, ...props }) {
  return (
    <Button type="submit" disabled={loading} className="rounded-sm" {...props}>
      {loading && <Loader2 size={16} className="mr-2 animate-spin" />}
      {children}
    </Button>
  );
}
